import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Crown, Sparkles, Lock, Tag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useT } from "@/i18n";

// Recovery modal fired exactly ONCE when a user's free trial auto-downgrades
// them to Free. Shows the Pro features they're losing + a permanent 20% off
// promo (TRIAL20) that pre-fills on the pricing checkout. Acks via the
// backend so a refresh / navigation can't re-trigger it.
export default function TrialEndedModal() {
  const { user, refresh } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  if (!user?.trial_just_ended || hidden) return null;

  const promoCode = user.trial_promo_code || "TRIAL20";

  const ack = async () => {
    setBusy(true);
    try {
      await api.post("/auth/trial-ended/ack");
      setHidden(true);
      // Refresh in the background so subsequent renders see trial_just_ended=false
      refresh?.().catch(() => { /* harmless */ });
    } finally {
      setBusy(false);
    }
  };

  const upgrade = async () => {
    await ack();
    // Pre-apply the 20% recovery promo on the pricing page.
    navigate(`/app/pricing?promo=${encodeURIComponent(promoCode)}`);
  };

  return (
    <div
      data-testid="trial-ended-modal"
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden relative">
        {/* Header */}
        <div
          className="p-6 text-white relative"
          style={{ background: "linear-gradient(135deg,#7c3aed 0%,#db2777 100%)" }}
        >
          <button
            onClick={ack}
            disabled={busy}
            data-testid="trial-ended-close"
            aria-label={t("trial_ended_close")}
            className="absolute right-3 top-3 p-1.5 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <Crown className="w-10 h-10 mb-3 opacity-90" />
          <h2 className="text-2xl font-black tracking-tight">{t("trial_ended_title")}</h2>
          <p className="mt-1 text-sm text-white/90">{t("trial_ended_subtitle")}</p>
        </div>

        {/* Body — what they're losing */}
        <div className="p-6">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5" /> {t("trial_ended_losing")}
          </div>
          <ul className="space-y-2 text-sm text-zinc-800">
            {["video", "interview", "image", "salesforce"].map((feat) => (
              <li key={feat} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                {t(`trial_ended_feat_${feat}`)}
              </li>
            ))}
          </ul>

          {/* Recovery promo */}
          <div className="mt-5 p-4 rounded-xl border-2 border-amber-300 bg-amber-50">
            <div className="flex items-center gap-2 mb-1">
              <Tag className="w-4 h-4 text-amber-700" />
              <span className="text-xs font-bold uppercase tracking-wider text-amber-900">
                {t("trial_ended_promo_eyebrow")}
              </span>
            </div>
            <div className="text-2xl font-black tracking-tight text-amber-900">
              {t("trial_ended_promo_headline")}
            </div>
            <div className="mt-1 text-xs text-amber-900/80">
              {t("trial_ended_promo_sub", { code: promoCode })}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <button
              onClick={upgrade}
              disabled={busy}
              data-testid="trial-ended-upgrade"
              className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-bold px-5 py-3 rounded-md text-white"
              style={{ background: "linear-gradient(135deg,#f59e0b,#ec4899)" }}
            >
              <Sparkles className="w-4 h-4" />
              {t("trial_ended_cta_upgrade", { code: promoCode })}
            </button>
            <button
              onClick={ack}
              disabled={busy}
              data-testid="trial-ended-dismiss"
              className="px-4 py-3 text-sm font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-md"
            >
              {t("trial_ended_cta_later")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
